import { useState } from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Filter, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import type { ActivityResponse } from "../lib/types";
import { dateTime, money, relative } from "../lib/format";

// One stream of everything that happened, newest first. Derived server-side
// from the rows themselves (see AdminActivityFeed), so it reaches back as far
// as the data does — not just as far as an events table would.
const DOT: Record<string, string> = {
  success: "bg-emerald-400",
  warning: "bg-amber-400",
  admin: "bg-purple-400",
  info: "bg-sky-400",
};

const CATEGORY_LABELS: Record<string, string> = {
  revenue: "Intäkt", demand: "Efterfrågan", supply: "Utbud",
  growth: "Tillväxt", quality: "Kvalitet", risk: "Risk", admin: "Admin",
};

export default function Activity() {
  const [kinds, setKinds] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading, refetch } = useInfiniteQuery({
    queryKey: ["activity", kinds],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api.get<ActivityResponse>("/superadmin/activity", {
        limit: 50,
        before: pageParam || undefined,
        kinds: kinds.length > 0 ? kinds : undefined,
      }),
    getNextPageParam: (last) => last.next_cursor,
    refetchInterval: 30_000,
  });

  const events = data?.pages.flatMap((p) => p.events) || [];
  const available = data?.pages[0]?.available_kinds || [];

  const toggle = (kind: string) =>
    setKinds((prev) => (prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind]));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Aktivitet</h1>
          <p className="text-sm text-ink-500">Allt som händer på plattformen, senaste först</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowFilters((v) => !v)} className="btn-ghost">
            <Filter className="h-3.5 w-3.5" />
            Filter {kinds.length > 0 && <span className="text-accent">({kinds.length})</span>}
          </button>
          <button onClick={() => refetch()} className="btn-ghost" disabled={isFetching}>
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="panel p-3">
          <div className="flex flex-wrap gap-1.5">
            {available.map((k) => (
              <button
                key={k.kind}
                onClick={() => toggle(k.kind)}
                className={`rounded border px-2 py-1 text-xs transition-colors ${
                  kinds.includes(k.kind)
                    ? "border-accent bg-accent/10 text-accent"
                    : "border-ink-700 text-ink-400 hover:text-ink-200"
                }`}
              >
                {k.label}
                <span className="ml-1 text-ink-600">{CATEGORY_LABELS[k.category] || k.category}</span>
              </button>
            ))}
          </div>
          {kinds.length > 0 && (
            <button onClick={() => setKinds([])} className="mt-2 text-xs text-ink-500 hover:text-ink-300">
              Rensa filter
            </button>
          )}
        </div>
      )}

      <div className="panel divide-y divide-ink-850">
        {isLoading &&
          Array.from({ length: 10 }).map((_, i) => <div key={i} className="h-14 animate-pulse bg-ink-850/40" />)}

        {!isLoading && events.length === 0 && (
          <div className="p-8 text-center text-sm text-ink-600">Inget att visa för valda filter.</div>
        )}

        {events.map((event) => {
          const inner = (
            <div className="flex items-start gap-3 px-3.5 py-2.5 transition-colors hover:bg-ink-850/60">
              <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${DOT[event.severity] || "bg-ink-500"}`} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="text-xs font-medium text-ink-500">{event.label}</span>
                  <span className="truncate text-sm text-ink-200">{event.title}</span>
                  {event.amount ? (
                    <span className="num text-xs text-emerald-400">{money(event.amount)}</span>
                  ) : null}
                </div>
                {event.subtitle && <div className="truncate text-xs text-ink-500">{event.subtitle}</div>}
              </div>
              <div className="shrink-0 text-right">
                <div className="num text-xs text-ink-400">{relative(event.occurred_at)}</div>
                <div className="num text-[10px] text-ink-600">{dateTime(event.occurred_at)}</div>
              </div>
            </div>
          );

          return event.resource ? (
            <Link key={event.id} to={`/r/${event.resource}/${event.record_id}`} className="block">
              {inner}
            </Link>
          ) : (
            <div key={event.id}>{inner}</div>
          );
        })}
      </div>

      {hasNextPage && (
        <button onClick={() => fetchNextPage()} disabled={isFetching} className="btn-ghost w-full justify-center">
          {isFetching ? "Hämtar…" : "Visa äldre"}
        </button>
      )}
    </div>
  );
}
